import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, ClipboardList, BarChart3, Settings, LogOut, Download, Shield, Key, Eye, EyeOff, AlertTriangle, Sun, Moon, Plus, Trash2, Save, Palette, Rocket, Trophy, Heart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useExperienceTheme } from '../context/ExperienceThemeContext';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { currentTheme, experienceTheme } = useExperienceTheme();
  const [stats, setStats] = useState({
    mission_streak: 0,
    total_courage_logs: 0,
    bridge_signals_sent: 0,
    proactive_missions_completed: 0,
    mission_day: 1,
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [maydaySettingsOpen, setMaydaySettingsOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Emergency Info State
  const [emergencyInfo, setEmergencyInfo] = useState({
    name: '',
    age: '',
    birthdate: '',
    address: '',
    medical_conditions: '',
    allergies: '',
    trigger_points: '',
    connection_points: '',
    emergency_contacts: [
      { name: '', relationship: 'Parent', phone: '', email: '' },
      { name: '', relationship: 'Parent', phone: '', email: '' }
    ]
  });
  const [savingEmergency, setSavingEmergency] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchEmergencyInfo();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/stats`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmergencyInfo = async () => {
    try {
      const response = await fetch(`${API}/emergency-info`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setEmergencyInfo(data);
      }
    } catch (error) {
      console.error('Failed to fetch emergency info:', error);
    }
  };

  const handleSaveEmergencyInfo = async () => {
    setSavingEmergency(true);
    try {
      const response = await fetch(`${API}/emergency-info`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(emergencyInfo),
      });

      if (response.ok) {
        toast.success('Emergency information saved!');
      } else {
        toast.error('Failed to save emergency information');
      }
    } catch (error) {
      toast.error('Failed to save emergency information');
    } finally {
      setSavingEmergency(false);
    }
  };

  const addEmergencyContact = () => {
    setEmergencyInfo({
      ...emergencyInfo,
      emergency_contacts: [
        ...emergencyInfo.emergency_contacts,
        { name: '', relationship: '', phone: '', email: '' }
      ]
    });
  };

  const removeEmergencyContact = (index) => {
    if (emergencyInfo.emergency_contacts.length > 1) {
      const newContacts = emergencyInfo.emergency_contacts.filter((_, i) => i !== index);
      setEmergencyInfo({ ...emergencyInfo, emergency_contacts: newContacts });
    }
  };

  const updateEmergencyContact = (index, field, value) => {
    const newContacts = [...emergencyInfo.emergency_contacts];
    newContacts[index] = { ...newContacts[index], [field]: value };
    setEmergencyInfo({ ...emergencyInfo, emergency_contacts: newContacts });
  };

  const handleExport = async () => {
    try {
      const response = await fetch(`${API}/export`, {
        credentials: 'include',
      });
      const data = await response.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mission-data-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      const response = await fetch(`${API}/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          current_password: passwordForm.currentPassword,
          new_password: passwordForm.newPassword,
        }),
      });

      if (response.ok) {
        toast.success('Password changed successfully');
        setPasswordDialogOpen(false);
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to change password');
      }
    } catch (error) {
      toast.error('Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  // Theme-specific colors from context
  const colors = currentTheme?.colors || {
    primary: '#00f3ff',
    secondary: '#bc13fe',
    accent: '#ff00aa',
  };

  // Space Theme Icons
  const SpaceCommunicationIcon = () => (
    <div className="relative">
      <div className="absolute inset-0 bg-cyan-500 blur-xl opacity-50 rounded-full" />
      <div className="relative w-10 h-10 md:w-12 md:h-12 flex items-center justify-center">
        <svg viewBox="0 0 24 24" className="w-8 h-8 md:w-10 md:h-10" fill="none" strokeWidth="1.5">
          <defs>
            <linearGradient id="space-comm" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#00f3ff" />
              <stop offset="100%" stopColor="#bc13fe" />
            </linearGradient>
          </defs>
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" fill="rgba(0, 243, 255, 0.15)" stroke="url(#space-comm)" />
          <circle cx="9" cy="10" r="1" fill="#00f3ff" />
          <circle cx="12" cy="10" r="1" fill="#00f3ff" />
          <circle cx="15" cy="10" r="1" fill="#00f3ff" />
        </svg>
      </div>
    </div>
  );

  const SpaceUpdateIcon = () => (
    <div className="relative">
      <div className="absolute inset-0 bg-purple-500 blur-xl opacity-50 rounded-full" />
      <div className="relative w-10 h-10 md:w-12 md:h-12 flex items-center justify-center">
        <svg viewBox="0 0 24 24" className="w-8 h-8 md:w-10 md:h-10" fill="none" strokeWidth="1.5">
          <defs>
            <linearGradient id="space-update" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#bc13fe" />
              <stop offset="100%" stopColor="#00f3ff" />
            </linearGradient>
          </defs>
          <rect x="3" y="3" width="18" height="18" rx="2" fill="rgba(188, 19, 254, 0.15)" stroke="url(#space-update)" />
          <path d="M9 11l3 3L22 4" stroke="#bc13fe" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  );

  const SpaceStatsIcon = () => (
    <div className="relative">
      <div className="absolute inset-0 bg-pink-500 blur-xl opacity-50 rounded-full" />
      <div className="relative w-10 h-10 md:w-12 md:h-12 flex items-center justify-center">
        <svg viewBox="0 0 24 24" className="w-8 h-8 md:w-10 md:h-10" fill="none">
          <defs>
            <linearGradient id="space-stats" x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#ff00aa" />
              <stop offset="100%" stopColor="#00f3ff" />
            </linearGradient>
          </defs>
          <rect x="3" y="12" width="4" height="9" rx="1" fill="url(#space-stats)" opacity="0.8" />
          <rect x="10" y="8" width="4" height="13" rx="1" fill="url(#space-stats)" />
          <rect x="17" y="4" width="4" height="17" rx="1" fill="url(#space-stats)" opacity="0.9" />
        </svg>
      </div>
    </div>
  );

  // Sports Theme Icons - Bold, athletic, sports-oriented
  const SportsHuddleIcon = () => (
    <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg">
      <svg viewBox="0 0 48 48" className="w-10 h-10 md:w-12 md:h-12" fill="none">
        {/* Megaphone/Coach */}
        <path d="M10 20L26 12V36L10 28V20Z" fill="white" />
        <path d="M26 18H38C40 18 42 20 42 22V26C42 28 40 30 38 30H26V18Z" fill="white" opacity="0.9" />
        <circle cx="7" cy="24" r="3" fill="white" />
        {/* Sound waves */}
        <path d="M32 14C36 16 38 20 38 24C38 28 36 32 32 34" stroke="white" strokeWidth="2" strokeLinecap="round" opacity="0.7" />
        <path d="M35 10C41 13 44 18 44 24C44 30 41 35 35 38" stroke="white" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
      </svg>
    </div>
  );

  const SportsCheckinIcon = () => (
    <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center shadow-lg">
      <svg viewBox="0 0 48 48" className="w-10 h-10 md:w-12 md:h-12" fill="none">
        {/* Clipboard with sports play */}
        <rect x="8" y="4" width="32" height="40" rx="4" fill="white" />
        <rect x="16" y="2" width="16" height="6" rx="2" fill="#1E40AF" />
        {/* Play diagram */}
        <circle cx="24" cy="24" r="3" fill="#EA580C" />
        <circle cx="16" cy="32" r="2" fill="#1E40AF" />
        <circle cx="32" cy="32" r="2" fill="#1E40AF" />
        <circle cx="16" cy="18" r="2" fill="#1E40AF" />
        <circle cx="32" cy="18" r="2" fill="#1E40AF" />
        <path d="M24 24L16 32M24 24L32 32M24 24L16 18M24 24L32 18" stroke="#1E40AF" strokeWidth="1.5" strokeDasharray="2 2" />
      </svg>
    </div>
  );

  const SportsStatsIcon = () => (
    <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center shadow-lg">
      <svg viewBox="0 0 48 48" className="w-10 h-10 md:w-12 md:h-12" fill="none">
        {/* Trophy */}
        <path d="M14 8H34V14C34 22 30 28 24 28C18 28 14 22 14 14V8Z" fill="white" />
        <rect x="20" y="28" width="8" height="6" fill="white" />
        <rect x="16" y="34" width="16" height="4" rx="1" fill="white" />
        {/* Trophy handles */}
        <path d="M14 10H8C8 10 6 10 6 14C6 18 8 20 12 20H14" stroke="white" strokeWidth="2.5" />
        <path d="M34 10H40C40 10 42 10 42 14C42 18 40 20 36 20H34" stroke="white" strokeWidth="2.5" />
        {/* Star on trophy */}
        <path d="M24 12L25.5 16H29L26 18.5L27.5 23L24 20L20.5 23L22 18.5L19 16H22.5L24 12Z" fill="#F59E0B" />
      </svg>
    </div>
  );

  // Pet Theme Icons - Cartoony, fun, cute animals
  const PetTalkIcon = () => (
    <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-gradient-to-br from-pink-400 to-pink-500 flex items-center justify-center shadow-lg">
      <svg viewBox="0 0 48 48" className="w-10 h-10 md:w-12 md:h-12" fill="none">
        {/* Cute dog face */}
        <ellipse cx="24" cy="26" rx="14" ry="12" fill="white" />
        {/* Floppy ears */}
        <ellipse cx="12" cy="18" rx="6" ry="10" fill="#F9A8D4" />
        <ellipse cx="36" cy="18" rx="6" ry="10" fill="#F9A8D4" />
        {/* Eyes */}
        <circle cx="19" cy="24" r="3" fill="#1F2937" />
        <circle cx="29" cy="24" r="3" fill="#1F2937" />
        <circle cx="20" cy="23" r="1" fill="white" />
        <circle cx="30" cy="23" r="1" fill="white" />
        {/* Nose */}
        <ellipse cx="24" cy="30" rx="3" ry="2" fill="#1F2937" />
        {/* Tongue */}
        <ellipse cx="24" cy="35" rx="3" ry="4" fill="#EC4899" />
        {/* Speech bubble */}
        <path d="M36 8C36 5 39 4 42 4C45 4 46 6 46 8C46 10 45 12 42 12C40 12 38 14 38 16L36 12C35 12 36 10 36 8Z" fill="white" stroke="#EC4899" strokeWidth="1.5" />
        <circle cx="40" cy="8" r="1" fill="#EC4899" />
        <circle cx="43" cy="8" r="1" fill="#EC4899" />
      </svg>
    </div>
  );

  const PetCheckinIcon = () => (
    <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-gradient-to-br from-purple-400 to-purple-500 flex items-center justify-center shadow-lg">
      <svg viewBox="0 0 48 48" className="w-10 h-10 md:w-12 md:h-12" fill="none">
        {/* Paw print with checkmark */}
        <ellipse cx="24" cy="30" rx="8" ry="7" fill="white" />
        <circle cx="14" cy="20" r="5" fill="white" />
        <circle cx="34" cy="20" r="5" fill="white" />
        <circle cx="18" cy="12" r="4" fill="white" />
        <circle cx="30" cy="12" r="4" fill="white" />
        {/* Checkmark */}
        <path d="M19 30L23 34L32 24" stroke="#8B5CF6" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );

  const PetProgressIcon = () => (
    <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-gradient-to-br from-amber-300 to-amber-400 flex items-center justify-center shadow-lg">
      <svg viewBox="0 0 48 48" className="w-10 h-10 md:w-12 md:h-12" fill="none">
        {/* Happy cat with stars */}
        <ellipse cx="24" cy="28" rx="12" ry="10" fill="white" />
        {/* Pointy ears */}
        <path d="M12 22L16 12L20 22" fill="white" />
        <path d="M36 22L32 12L28 22" fill="white" />
        <path d="M13 20L16 13L19 20" fill="#FCD34D" />
        <path d="M35 20L32 13L29 20" fill="#FCD34D" />
        {/* Happy closed eyes */}
        <path d="M18 26C18 26 20 28 22 26" stroke="#1F2937" strokeWidth="2" strokeLinecap="round" />
        <path d="M26 26C26 26 28 28 30 26" stroke="#1F2937" strokeWidth="2" strokeLinecap="round" />
        {/* Smile */}
        <path d="M20 32C20 32 24 36 28 32" stroke="#1F2937" strokeWidth="2" strokeLinecap="round" />
        {/* Whiskers */}
        <line x1="8" y1="28" x2="16" y2="30" stroke="#1F2937" strokeWidth="1" />
        <line x1="8" y1="32" x2="16" y2="32" stroke="#1F2937" strokeWidth="1" />
        <line x1="40" y1="28" x2="32" y2="30" stroke="#1F2937" strokeWidth="1" />
        <line x1="40" y1="32" x2="32" y2="32" stroke="#1F2937" strokeWidth="1" />
        {/* Stars */}
        <path d="M8 10L9 13L12 13L10 15L11 18L8 16L5 18L6 15L4 13L7 13L8 10Z" fill="#F59E0B" />
        <path d="M40 8L41 10L43 10L41.5 12L42 14L40 13L38 14L38.5 12L37 10L39 10L40 8Z" fill="#F59E0B" />
      </svg>
    </div>
  );

  // Get icons based on theme
  const getIcons = () => {
    if (experienceTheme === 'sports') {
      return {
        communication: SportsHuddleIcon,
        update: SportsCheckinIcon,
        stats: SportsStatsIcon,
      };
    }
    if (experienceTheme === 'pet') {
      return {
        communication: PetTalkIcon,
        update: PetCheckinIcon,
        stats: PetProgressIcon,
      };
    }
    return {
      communication: SpaceCommunicationIcon,
      update: SpaceUpdateIcon,
      stats: SpaceStatsIcon,
    };
  };

  const icons = getIcons();

  // Get themed labels from terminology
  const t = currentTheme?.terminology || {};

  const tools = [
    {
      id: 'communication',
      IconComponent: icons.communication,
      label: t.communication || 'Communication',
      description: t.communicationDesc || 'Send a signal',
      path: '/communication',
      testId: 'tool-communication',
    },
    {
      id: 'mission-update',
      IconComponent: icons.update,
      label: t.missionUpdate || 'Mission Update',
      description: t.missionUpdateDesc || 'Log your status',
      path: '/mission-update',
      testId: 'tool-mission-update',
    },
    {
      id: 'mission-stats',
      IconComponent: icons.stats,
      label: t.missionStats || 'Mission Stats',
      description: t.missionStatsDesc || 'View progress',
      path: '/mission-stats',
      testId: 'tool-mission-stats',
    },
  ];

  const statItems = [
    { label: t.streak || 'Mission Streak', value: stats.mission_streak, suffix: 'days' },
    { label: t.courageLogs || 'Courage Logs', value: stats.total_courage_logs, suffix: 'total' },
    { label: t.signalsSent || 'Signals Sent', value: stats.bridge_signals_sent, suffix: 'sent' },
  ];

  // Get theme icon for header
  const ThemeIcon = experienceTheme === 'sports' ? Trophy : experienceTheme === 'pet' ? Heart : Rocket;

  return (
    <div className="min-h-screen theme-bg">
      {/* Background gradient */}
      <div className="fixed inset-0 theme-gradient opacity-50 pointer-events-none" />

      {/* Header */}
      <header className="relative z-20 px-4 py-4 md:px-8 md:py-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ThemeIcon className="w-6 h-6" style={{ color: colors.primary }} />
            <div>
              <h1 className="text-gradient text-xl md:text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {t.appName || "Callen's Mission"}
              </h1>
              <p className="theme-text-muted text-sm">
                {t.dayLabel || 'Mission Day'} {stats.mission_day}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Mayday Button in Header */}
            <button
              onClick={() => navigate('/mayday')}
              data-testid="header-mayday-btn"
              className="w-10 h-10 rounded-full bg-red-600 hover:bg-red-500 flex items-center justify-center text-white transition-all hover:scale-105"
              title="Mayday - Emergency Info"
            >
              <AlertTriangle size={18} />
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="settings-menu-btn"
                  className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center theme-text-muted hover:theme-text hover:bg-white/10 transition-all"
                >
                  <Settings size={18} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2 border-b border-white/10">
                  <p className="theme-text font-medium text-sm">{user?.name || t.userRole || 'Pilot'}</p>
                  <p className="theme-text-muted text-xs truncate">{user?.email}</p>
                </div>
                <DropdownMenuItem
                  data-testid="settings-btn"
                  onClick={() => setSettingsOpen(true)}
                  className="theme-text-muted hover:theme-text cursor-pointer"
                >
                  <Settings size={16} className="mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="change-theme-btn"
                  onClick={() => navigate('/choose-theme')}
                  className="theme-text-muted hover:theme-text cursor-pointer"
                >
                  <Palette size={16} className="mr-2" />
                  Change Theme
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="mayday-settings-btn"
                  onClick={() => setMaydaySettingsOpen(true)}
                  className="text-red-400 hover:text-red-300 cursor-pointer"
                >
                  <AlertTriangle size={16} className="mr-2" />
                  {t.mayday || 'Mayday'} Settings
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="change-password-btn"
                  onClick={() => setPasswordDialogOpen(true)}
                  className="theme-text-muted hover:theme-text cursor-pointer"
                >
                  <Key size={16} className="mr-2" />
                  Change Password
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="export-btn"
                  onClick={handleExport}
                  className="theme-text-muted hover:theme-text cursor-pointer"
                >
                  <Download size={16} className="mr-2" />
                  Export Mission Data
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem
                    data-testid="admin-btn"
                    onClick={() => navigate('/admin')}
                    className="text-purple-400 hover:text-purple-300 cursor-pointer"
                  >
                    <Shield size={16} className="mr-2" />
                    Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem
                  data-testid="logout-btn"
                  onClick={handleLogout}
                  className="text-red-400 hover:text-red-300 cursor-pointer"
                >
                  <LogOut size={16} className="mr-2" />
                  End Session
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 px-4 md:px-8 pb-24">
        <div className="max-w-4xl mx-auto">
          {/* Welcome Message */}
          <div className="text-center py-8 md:py-12 animate-fade-in-up">
            <p className="theme-text-secondary text-lg">
              Welcome back, <span className="theme-text">{user?.name?.split(' ')[0] || t.userRole || 'Pilot'}</span>
            </p>
            <p className="theme-text-muted text-sm mt-1">
              {t.dashboard || 'Mission Control'} is ready when you are.
            </p>
          </div>

          {/* Tool Buttons - Theme-specific Icons */}
          <div className="grid grid-cols-3 gap-4 md:gap-8 max-w-xl mx-auto mb-12 stagger-children">
            {tools.map((tool) => (
              <button
                key={tool.id}
                data-testid={tool.testId}
                onClick={() => navigate(tool.path)}
                className="relative flex flex-col items-center p-2 md:p-4 rounded-2xl transition-all duration-300 ease-out hover:scale-105 active:scale-95"
              >
                <tool.IconComponent />
                <div className="mt-3 h-8 flex items-end justify-center">
                  <span className="theme-text-secondary text-[10px] md:text-sm font-medium text-center leading-tight">
                    {tool.label}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {/* Mission Stats Summary */}
          <div className="card-modern p-6 md:p-8 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            <h2 className="theme-text-muted text-sm font-medium mb-6 uppercase tracking-wider">
              {t.missionStats || 'Mission Stats'} Overview
            </h2>
            
            <div className="grid grid-cols-3 gap-4 md:gap-6">
              {statItems.map((stat, index) => (
                <div key={stat.label} className="text-center">
                  <p className="text-gradient text-2xl md:text-3xl font-bold">
                    {loading ? '—' : stat.value}
                  </p>
                  <p className="theme-text-muted text-xs md:text-sm mt-1">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-6 border-t border-white/5">
              <button
                onClick={() => navigate('/mission-stats')}
                style={{ color: colors.primary }}
                className="text-sm font-medium transition-colors opacity-80 hover:opacity-100"
              >
                View detailed stats →
              </button>
            </div>
          </div>

          {/* Encouragement */}
          <div className="text-center py-8 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            <p className="theme-text-muted text-sm">
              {t.encouragement || "Every mission starts with one step. You've got this, Pilot."}
            </p>
          </div>
        </div>
      </main>

      {/* Settings Dialog (Theme Toggle) */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="theme-text text-lg font-semibold flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Settings
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className="w-full flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all cursor-pointer"
            >
              <div className="flex items-center gap-3">
                {theme === 'dark' ? <Moon size={20} className="text-blue-400" /> : <Sun size={20} className="text-yellow-400" />}
                <div className="text-left">
                  <p className="theme-text font-medium">Theme</p>
                  <p className="theme-text-muted text-sm">{theme === 'dark' ? 'Dark Mode' : 'Light Mode'}</p>
                </div>
              </div>
              {/* Custom Toggle Switch */}
              <div className={`w-14 h-8 rounded-full p-1 transition-all ${theme === 'light' ? 'bg-yellow-400' : 'bg-blue-600'}`}>
                <div className={`w-6 h-6 rounded-full bg-white shadow-md transform transition-transform ${theme === 'light' ? 'translate-x-6' : 'translate-x-0'}`}>
                  {theme === 'dark' ? (
                    <Moon size={14} className="text-blue-600 m-1" />
                  ) : (
                    <Sun size={14} className="text-yellow-500 m-1" />
                  )}
                </div>
              </div>
            </button>

            <div className="p-4 rounded-xl bg-white/5 border border-white/10">
              <p className="theme-text-muted text-sm">
                Your theme preference is saved and synced across all your sessions.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mayday Settings Dialog */}
      <Dialog open={maydaySettingsOpen} onOpenChange={setMaydaySettingsOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="theme-text text-lg font-semibold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Mayday Settings
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Personal Information */}
            <div className="space-y-4">
              <h3 className="theme-text font-semibold text-sm uppercase tracking-wider">Personal Information</h3>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="theme-text-muted text-xs mb-1 block">Full Name</label>
                  <input
                    type="text"
                    value={emergencyInfo.name}
                    onChange={(e) => setEmergencyInfo({ ...emergencyInfo, name: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 theme-text text-sm focus:border-[#00f3ff]/50 focus:outline-none"
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <label className="theme-text-muted text-xs mb-1 block">Age</label>
                  <input
                    type="text"
                    value={emergencyInfo.age}
                    onChange={(e) => setEmergencyInfo({ ...emergencyInfo, age: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 theme-text text-sm focus:border-[#00f3ff]/50 focus:outline-none"
                    placeholder="Age"
                  />
                </div>
              </div>
              
              <div>
                <label className="theme-text-muted text-xs mb-1 block">Birthdate</label>
                <input
                  type="text"
                  value={emergencyInfo.birthdate}
                  onChange={(e) => setEmergencyInfo({ ...emergencyInfo, birthdate: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 theme-text text-sm focus:border-[#00f3ff]/50 focus:outline-none"
                  placeholder="MM/DD/YYYY"
                />
              </div>
              
              <div>
                <label className="theme-text-muted text-xs mb-1 block">Address</label>
                <textarea
                  value={emergencyInfo.address}
                  onChange={(e) => setEmergencyInfo({ ...emergencyInfo, address: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 theme-text text-sm focus:border-[#00f3ff]/50 focus:outline-none resize-none"
                  rows={2}
                  placeholder="Home address"
                />
              </div>
            </div>

            {/* Emergency Contacts */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="theme-text font-semibold text-sm uppercase tracking-wider">Emergency Contacts</h3>
                <button
                  onClick={addEmergencyContact}
                  className="text-[#00f3ff] hover:text-[#00f3ff]/80 text-sm flex items-center gap-1"
                >
                  <Plus size={14} /> Add Contact
                </button>
              </div>
              
              {emergencyInfo.emergency_contacts.map((contact, index) => (
                <div key={index} className="p-3 rounded-xl bg-white/5 border border-white/10 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="theme-text-muted text-xs">Contact {index + 1}</span>
                    {emergencyInfo.emergency_contacts.length > 1 && (
                      <button
                        onClick={() => removeEmergencyContact(index)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={contact.name}
                      onChange={(e) => updateEmergencyContact(index, 'name', e.target.value)}
                      className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 theme-text text-sm focus:border-[#00f3ff]/50 focus:outline-none"
                      placeholder="Name"
                    />
                    <input
                      type="text"
                      value={contact.relationship}
                      onChange={(e) => updateEmergencyContact(index, 'relationship', e.target.value)}
                      className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 theme-text text-sm focus:border-[#00f3ff]/50 focus:outline-none"
                      placeholder="Relationship"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="tel"
                      value={contact.phone}
                      onChange={(e) => updateEmergencyContact(index, 'phone', e.target.value)}
                      className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 theme-text text-sm focus:border-[#00f3ff]/50 focus:outline-none"
                      placeholder="Phone"
                    />
                    <input
                      type="email"
                      value={contact.email}
                      onChange={(e) => updateEmergencyContact(index, 'email', e.target.value)}
                      className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 theme-text text-sm focus:border-[#00f3ff]/50 focus:outline-none"
                      placeholder="Email"
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Medical Information */}
            <div className="space-y-4">
              <h3 className="theme-text font-semibold text-sm uppercase tracking-wider">Medical Information</h3>
              
              <div>
                <label className="theme-text-muted text-xs mb-1 block">Medical Conditions</label>
                <textarea
                  value={emergencyInfo.medical_conditions}
                  onChange={(e) => setEmergencyInfo({ ...emergencyInfo, medical_conditions: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 theme-text text-sm focus:border-[#00f3ff]/50 focus:outline-none resize-none"
                  rows={2}
                  placeholder="Any medical conditions..."
                />
              </div>
              
              <div>
                <label className="theme-text-muted text-xs mb-1 block">Allergies</label>
                <textarea
                  value={emergencyInfo.allergies}
                  onChange={(e) => setEmergencyInfo({ ...emergencyInfo, allergies: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 theme-text text-sm focus:border-[#00f3ff]/50 focus:outline-none resize-none"
                  rows={2}
                  placeholder="Any allergies..."
                />
              </div>
            </div>

            {/* Behavioral Information */}
            <div className="space-y-4">
              <h3 className="theme-text font-semibold text-sm uppercase tracking-wider">Behavioral Information</h3>
              
              <div>
                <label className="theme-text-muted text-xs mb-1 block flex items-center gap-2">
                  <span className="text-orange-400">⚠️</span> Trigger Points (Things to AVOID)
                </label>
                <textarea
                  value={emergencyInfo.trigger_points}
                  onChange={(e) => setEmergencyInfo({ ...emergencyInfo, trigger_points: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-orange-500/10 border border-orange-500/30 theme-text text-sm focus:border-orange-500/50 focus:outline-none resize-none"
                  rows={3}
                  placeholder="Things that may cause stress, anxiety, or escalation..."
                />
              </div>
              
              <div>
                <label className="theme-text-muted text-xs mb-1 block flex items-center gap-2">
                  <span className="text-green-400">✨</span> Connection Points (Things that HELP)
                </label>
                <textarea
                  value={emergencyInfo.connection_points}
                  onChange={(e) => setEmergencyInfo({ ...emergencyInfo, connection_points: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-green-500/10 border border-green-500/30 theme-text text-sm focus:border-green-500/50 focus:outline-none resize-none"
                  rows={3}
                  placeholder="Topics, activities, or approaches that help calm and connect..."
                />
              </div>
            </div>

            {/* Save Button */}
            <button
              onClick={handleSaveEmergencyInfo}
              disabled={savingEmergency}
              className="w-full py-3 rounded-xl bg-red-600 hover:bg-red-500 text-white font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-50"
            >
              <Save size={18} />
              {savingEmergency ? 'Saving...' : 'Save Mayday Information'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="theme-text text-lg font-semibold">
              Change Password
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4 py-4">
            <div>
              <label className="theme-text-muted text-sm mb-2 block">Current Password</label>
              <div className="relative">
                <input
                  data-testid="current-password-input"
                  type={showPasswords.current ? 'text' : 'password'}
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 theme-text placeholder-white/30 focus:border-[#00f3ff]/50 focus:outline-none transition-colors pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 theme-text-muted hover:theme-text"
                >
                  {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label className="theme-text-muted text-sm mb-2 block">New Password</label>
              <div className="relative">
                <input
                  data-testid="new-password-input"
                  type={showPasswords.new ? 'text' : 'password'}
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 theme-text placeholder-white/30 focus:border-[#00f3ff]/50 focus:outline-none transition-colors pr-10"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 theme-text-muted hover:theme-text"
                >
                  {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label className="theme-text-muted text-sm mb-2 block">Confirm New Password</label>
              <div className="relative">
                <input
                  data-testid="confirm-password-input"
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 theme-text placeholder-white/30 focus:border-[#00f3ff]/50 focus:outline-none transition-colors pr-10"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 theme-text-muted hover:theme-text"
                >
                  {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <p className="theme-text-muted text-xs">
              Password must be at least 6 characters long.
            </p>
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  setPasswordDialogOpen(false);
                  setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                }}
                className="btn-secondary flex-1 py-3"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={changingPassword}
                className="btn-primary flex-1 py-3 disabled:opacity-50"
                data-testid="submit-password-change"
              >
                {changingPassword ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Home;
